/**
 * TanStack Query hooks for VIP data with WebSocket invalidation
 * 
 * VIP status can be modified by admins, so we use WebSocket events
 * to invalidate the cache when changes occur.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';
import { apiClient } from '@/lib/api-client';
import { websocketClient } from '@/lib/websocket-client';
import { useAuth } from '@/contexts/AuthContext';
import { storage } from '@/lib/storage';

export interface VipTier {
  id: string | number;
  tier_name: string;
  tier_level: number;
  tier_color: string;
  monthly_gc_required: number;
  points_required?: number;
  bonus_multiplier?: number;
  daily_withdrawal_count_limit?: number;
  daily_withdrawal_prize_limit?: number;
  tier_reach_reward_gc?: number;
  tier_reach_reward_sc?: number;
  weekly_reward_percentage?: number;
  max_weekly_reward_gc?: number;
  max_weekly_reward_sc?: number;
  vip_access?: boolean;
  tier_icon_url?: string | null;
}

export interface VipStatus {
  current_tier_id: number;
  monthly_vip_points?: number;
  total_wagered: number;
  total_sc_wagered?: number;
  tier_name?: string;
  tier_level?: number;
  tier_color?: string;
  monthly_gc_required?: number;
  points_required?: number;
  tier_reach_reward_gc?: number;
  tier_reach_reward_sc?: number;
  weekly_reward_percentage?: number;
  max_weekly_reward_gc?: number;
  max_weekly_reward_sc?: number;
  vip_access?: boolean;
  tier_icon_url?: string | null;
  tier_expires_at?: string | null;
  tier_expired?: boolean;
  days_until_expiration?: number | null;
  show_tier_expiration_warning?: boolean;
  next_tier_name?: string | null;
  next_tier_points_required?: number | null;
  points_to_next_tier?: number;
  progress_percent?: number;
  weights?: {
    gc_wager_weight: number;
    sc_wager_weight: number;
  };
  period_name?: string;
  period_reset_date?: string;
  days_until_reset?: number;
  hours_until_reset?: number;
  is_production?: boolean;
  evaluations_remaining?: number | null;
}

export interface PreviousBonusInfo {
  alreadyClaimed: boolean;
  claimedAt: string | null;
  createdAt: string;
}

export interface VipTierUpgrade {
  oldTier: {
    id: string;
    tier_name: string;
    tier_level: number;
    tier_color: string;
  };
  newTier: {
    id: string;
    tier_name: string;
    tier_level: number;
    tier_color: string;
    tier_reach_reward_gc: number;
    tier_reach_reward_sc: number;
  };
  bonusAwarded: boolean;
  previousBonusInfo?: PreviousBonusInfo | null;
  isManualUpgrade?: boolean;
}

const TEN_MINUTES = 10 * 60 * 1000;

/**
 * Response type for unified VIP data endpoint
 */
export interface VipDataResponse {
  tiers: VipTier[];
  vipStatus: VipStatus | null;
  tierUpgrade?: VipTierUpgrade | null;
}

/**
 * Helper to normalize VIP status response
 */
function normalizeVipStatus(status: VipStatus | null): VipStatus | null {
  if (!status) return null;
  return {
    ...status,
    current_tier_id: Number(status.current_tier_id) || 0,
    monthly_vip_points: Number(status.monthly_vip_points) || 0,
    total_wagered: Number(status.total_wagered) || 0,
    total_sc_wagered: Number(status.total_sc_wagered) || 0,
    points_to_next_tier: Number(status.points_to_next_tier) || 0,
    progress_percent: Number(status.progress_percent) || 0,
    points_required: Number(status.points_required) || Number(status.monthly_gc_required) || 0,
    weights: status.weights || { gc_wager_weight: 1.0, sc_wager_weight: 1000.0 },
  };
}

/**
 * Unified VIP data query - fetches tiers and status in one call
 * 
 * This consolidates 2 separate API calls into 1:
 * - /loyalty/vip-tiers
 * - /loyalty/vip-status
 * 
 * Uses the new /loyalty/vip-data endpoint
 */
export function useVipDataQuery() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  // Subscribe to WebSocket events for cache invalidation
  useEffect(() => {
    if (!user) return;

    const token = storage.getItem('auth_token') || storage.getItem('token');
    if (!token) return;

    websocketClient.connect(token).catch(() => {});

    const handleVipUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['vip', 'data'] });
    };

    const handleBalanceUpdate = () => {
      queryClient.invalidateQueries({ queryKey: ['vip', 'data'] });
    };

    const unsubscribeVip = websocketClient.subscribe('vip:updated', handleVipUpdate);
    const unsubscribeBalance = websocketClient.subscribe('balance:update', handleBalanceUpdate);

    return () => {
      unsubscribeVip();
      unsubscribeBalance();
    };
  }, [user, queryClient]);

  return useQuery({
    queryKey: ['vip', 'data'],
    queryFn: async (): Promise<VipDataResponse> => {
      const response = await apiClient.get<VipDataResponse>('/loyalty/vip-data');
      
      return {
        tiers: response?.tiers || [],
        vipStatus: normalizeVipStatus(response?.vipStatus || null),
        tierUpgrade: response?.tierUpgrade || null,
      };
    },
    staleTime: TEN_MINUTES,
    gcTime: TEN_MINUTES * 3,
    enabled: !!user,
  });
}

